// components/CallNotesTimeline.jsx
import React, { useState, useEffect } from "react";
import { Phone, Clock, User, Calendar } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CallNotesTimeline = ({ entityType, entityId }) => {
  const { isDarkMode } = useTheme();
  const [callNotes, setCallNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCallNotes();
  }, [entityType, entityId]);

  const fetchCallNotes = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCallNotes(entityType, entityId);
      setCallNotes(response.data || []);
    } catch (error) {
      console.error("Error fetching call notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: "text-green-400 bg-green-400/10",
      no_answer: "text-yellow-400 bg-yellow-400/10",
      busy: "text-orange-400 bg-orange-400/10",
      voicemail: "text-blue-400 bg-blue-400/10",
      failed: "text-red-400 bg-red-400/10",
    };
    return colors[status] || "text-gray-400 bg-gray-400/10";
  };

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <div
        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        Loading call history...
      </div>
    );
  }

  if (callNotes.length === 0) {
    return (
      <div
        className={`text-center py-8 ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      >
        <Phone size={48} className="mx-auto mb-2 opacity-50" />
        <p className="text-sm">No call history yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {callNotes.map((callNote) => (
        <div
          key={callNote.id}
          className={`p-4 rounded-lg border ${
            isDarkMode
              ? "bg-gray-800/50 border-gray-700/50"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <Phone size={16} className="text-blue-400" />
              <span
                className={`font-medium text-sm break-words min-w-0 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {callNote.title}
              </span>
            </div>
            <span
              className={`text-xs px-2 py-1 rounded ${getStatusColor(callNote.callStatus)}`}
            >
              {callNote.callStatus.replace("_", " ")}
            </span>
          </div>

          <p
            className={`text-sm mb-3 break-words min-w-0 ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {callNote.description}
          </p>

          <div
            className={`flex items-center gap-4 text-xs ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {callNote.caller && (
              <div className="flex items-center gap-1">
                <User size={12} />
                <span>{callNote.caller.name}</span>
              </div>
            )}
            {callNote.callDuration && (
              <div className="flex items-center gap-1">
                <Clock size={12} />
                <span>{formatDuration(callNote.callDuration)}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Calendar size={12} />
              <span>{new Date(callNote.createdAt).toLocaleString()}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CallNotesTimeline;
